import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../redux/store";
import HobbyArt from "./../../../assets/img/giftshop/landing/hobby-art.png";
import SigninModal from "../../../modal/SigninModal";
import ForgotPasswordModal from "../../../modal/ForgotPasswordModal";
import VerificationModal from "../../../modal/VerificationModal";
import ResetModal from "../../../modal/ResetModal";
import SuccessModal from "../../../modal/SuccessModal";
import SignupModal from "../../../modal/SignupModal";
import EmailVerificationModal from "../../../modal/EmailVerificationModal";
import LoaderSaveModal from "../../../modal/LoaderSaveModal";
import { AuthUser } from "../../../modal/auth/types";
import { authService } from "../../../modal/auth/authService";
import { persistAuthUser } from "../../../utils/authSession";
import { resolveArtDisplayName } from "../../../utils/artDisplayName";
import { finalizeOrderWithGeneratedArt } from "../../../utils/finalizeOrderWithGeneratedArt";
import {
  persistHobbyCharacterFavouriteUrls,
  saveArtAndHobbyFavouritesAfterLogin,
} from "../../../utils/hobbyGenerationNavigation";
import { resolveStationRouteSlug } from "../../../utils/stationFlowStorage";
import {
  readSelectedAiSelfieArtFromStorage,
  resolveHobbyCaricatureRouteSlug,
  resolveSelectedAiSelfieArtFavouriteType,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
  shouldUsePetCaricatureBackgroundFlow,
} from "../../../services/stylePromptResolver";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";
import {
  isTransparentHobbyBackgroundName,
  readFavouriteTransparencyFlag,
} from "../../../services/caricatureHobbyService";

type SaveFavoritePhase = "save-prompt" | "claim-instructions";

const SaveFavorite = () => {
  const minLoaderDurationMs = 2500;
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [loading, setLoading] = useState(false);
  const [saveModal, setSaveModal] = useState<boolean>(false);
  const [signinModal, setSigninModal] = useState<boolean>(false);
  const [signupModal, setSignupModal] = useState<boolean>(false);
  const [emailVerificationModal, setEmailVerificationModal] = useState<boolean>(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState<boolean>(false);
  const [verificationModal, setVerificationModal] = useState<boolean>(false);
  const [resetModal, setResetModal] = useState<boolean>(false);
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [savePhase, setSavePhase] = useState<SaveFavoritePhase>("save-prompt");
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const { ticketId, hobbyPersonCompletedResults } = useSelector((s: RootState) => ({
    ticketId: s.qrScan?.ticketId ?? null,
    hobbyPersonCompletedResults: s.uploadFlow?.hobbyPersonCompletedResults ?? [],
  }));

  useEffect(() => {
    const routeUrls = (location.state as { hobbyCharacterFavouriteUrls?: string[] } | null)
      ?.hobbyCharacterFavouriteUrls;
    if (Array.isArray(routeUrls) && routeUrls.length > 0) {
      persistHobbyCharacterFavouriteUrls(routeUrls);
    }
  }, [location.state]);


  const selectedImageUrl = useMemo(() => {
    const fromState = (location.state as any)?.selectedHobbyGeneratedImage;
    if (typeof fromState === "string" && fromState.trim().length > 0) {
      return fromState;
    }
    const fromFavourite = localStorage.getItem("favouriteImg");
    if (typeof fromFavourite === "string" && fromFavourite.trim().length > 0) {
      return fromFavourite;
    }
    try {
      const generationResult = JSON.parse(localStorage.getItem("hobbyCaricatureGenerationResult") || "{}");
      const firstGenerated = Array.isArray(generationResult?.imageUrls) ? generationResult.imageUrls[0] : null;
      if (typeof firstGenerated === "string" && firstGenerated.trim().length > 0) {
        return firstGenerated;
      }
    } catch {
      /* ignore parse errors */
    }
    return HobbyArt;
  }, [location.state]);

  const isTransparentArtwork = useMemo(() => {
    const fromState = (location.state as { isTransparentHobbyBackground?: boolean } | null)
      ?.isTransparentHobbyBackground;
    if (typeof fromState === "boolean") return fromState;

    const selectedBg = (location.state as { selectedHobbyBackground?: { name?: string } | null } | null)
      ?.selectedHobbyBackground;
    if (isTransparentHobbyBackgroundName(selectedBg?.name)) return true;

    try {
      const storedBg = JSON.parse(localStorage.getItem("selectedCaricatureHobbyBackground") || "null");
      if (isTransparentHobbyBackgroundName(storedBg?.name)) return true;
    } catch {
      /* ignore */
    }

    return readFavouriteTransparencyFlag();
  }, [location.state]);

  useEffect(() => {
    setPreviewLoaded(false);
  }, [selectedImageUrl]);

  const stationData = useMemo(() => {
    try {
      const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)
        ?.stationData;
      if (fromNav && typeof fromNav === "object") {
        return fromNav;
      }
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return {};
    }
  }, [location.state]);

  const selectedArt = useMemo(() => {
    const fromNav = (location.state as { selectedArt?: { name?: string | null } } | null)
      ?.selectedArt;
    if (fromNav?.name) {
      return { ...fromNav, name: fromNav.name ?? undefined };
    }
    return readSelectedAiSelfieArtFromStorage();
  }, [location.state]);

  const isPetFlow = shouldUsePetCaricatureBackgroundFlow(
    selectedArt,
    stationData as { type?: string | null; name?: string | null }
  );

  const artDisplayName = useMemo(
    () =>
      resolveArtDisplayName({
        selectedArt: selectedArt ?? undefined,
        stationData: stationData as { name?: string },
        fallback: isPetFlow ? "Pet Caricature" : "Caricature Hobby",
      }),
    [selectedArt, stationData, isPetFlow]
  );

  /** Favourites API `type` — same label shown in UI (e.g. Pet Caricature). */
  const favouriteType = useMemo(() => {
    const fromResolver = resolveSelectedAiSelfieArtFavouriteType(selectedArt)?.trim();
    if (fromResolver && fromResolver.toLowerCase() !== "hobby caricature") {
      return fromResolver;
    }
    return artDisplayName;
  }, [selectedArt, artDisplayName]);

  const saveHeadings = useMemo(
    () => ({
      "save-prompt": (
        <>
          Would you like to save your
          <br />
          {artDisplayName}?
        </>
      ),
      "claim-instructions": `Please claim your ${artDisplayName} at the printing station`,
    }),
    [artDisplayName]
  );

  const showActionButtons = savePhase === "save-prompt";

  const persistSelectedImage = () => {
    const artUrl = selectedImageUrl;
    if (artUrl) {
      try {
        localStorage.setItem("favouriteImg", artUrl);
      } catch {
        /* ignore storage errors */
      }
    }
    return artUrl;
  };

  const routeSlug = useMemo(() => {
    if (isPetFlow) {
      return (
        resolveStationRouteSlug(
          stationData as { type?: string; name?: string },
          slug,
          selectedArt
        ) || slug || "pet-caricature"
      );
    }
    if (
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt() ||
      (stationData as { type?: string })?.type === "hobby-caricature" ||
      slug === "hobby-caricature" ||
      slug === "caricature-cartoon"
    ) {
      return resolveHobbyCaricatureRouteSlug(slug);
    }
    return (
      resolveStationRouteSlug(
        stationData as { type?: string; name?: string },
        slug,
        selectedArt
      ) || slug || "hobby-caricature"
    );
  }, [isPetFlow, selectedArt, slug, stationData]);

  // If we landed on the parent category slug, correct the URL in place.
  useEffect(() => {
    if (!slug || slug === routeSlug) return;
    if (slug !== "caricature-cartoon") return;
    navigate(`/${routeSlug}/save-favorite`, {
      replace: true,
      state: location.state,
    });
  }, [slug, routeSlug, navigate, location.state]);

  const navigateToPrintFrameSelection = useCallback(() => {
    const artUrl = persistSelectedImage();
    navigate(`/${routeSlug}/print-frame-selection`, {
      state: {
        stationData,
        snapshot: artUrl,
      },
    });
  }, [navigate, routeSlug, selectedImageUrl, stationData]);

  const attachArtToPaidOrder = useCallback(async (): Promise<boolean> => {
    const trimmed = selectedImageUrl?.trim();
    if (!trimmed || trimmed === HobbyArt) return true;

    setIsUpdatingOrder(true);
    try {
      const result = await finalizeOrderWithGeneratedArt(trimmed);
      if (!result.success && !result.skipped) {
        window.alert(
          result.error || "Failed to attach your artwork to the order. Please try again."
        );
        return false;
      }
      return true;
    } finally {
      setIsUpdatingOrder(false);
    }
  }, [selectedImageUrl]);

  const handleNo = async () => {
    const attached = await attachArtToPaidOrder();
    if (!attached) return;

    navigateToPrintFrameSelection();
  };

  const handleYes = async () => {
    const attached = await attachArtToPaidOrder();
    if (!attached) return;

    setSigninModal(true);
  };

  useEffect(() => {
    if (savePhase !== "claim-instructions") return;

    const timer = setTimeout(() => {
      navigateToPrintFrameSelection();
    }, 3000);

    return () => clearTimeout(timer);
  }, [savePhase, navigateToPrintFrameSelection]);

  const handleAuthSuccess = async (user: AuthUser) => {
    persistAuthUser(user);

    setSigninModal(false);
    setSignupModal(false);
    setEmailVerificationModal(false);
    setForgotPasswordModal(false);
    setVerificationModal(false);
    setResetModal(false);
    setSuccessModal(false);
    setSaveModal(false);

    setLoading(true);
    const minimumDelay = new Promise((resolve) => setTimeout(resolve, minLoaderDurationMs));
    const routeCharacterUrls = (location.state as { hobbyCharacterFavouriteUrls?: string[] } | null)
      ?.hobbyCharacterFavouriteUrls;
    const reduxCharacterUrls = (hobbyPersonCompletedResults || []).filter(
      (url): url is string => typeof url === "string" && url.trim().length > 0
    );

    try {
      const [, saveResult] = await Promise.all([
        minimumDelay,
        saveArtAndHobbyFavouritesAfterLogin({
          user,
          artUrl: selectedImageUrl,
          ticketId: ticketId || undefined,
          routeCharacterUrls,
          fallbackCharacterUrls: reduxCharacterUrls,
          type: favouriteType,
        }),
      ]);

      if (!saveResult.success) {
        window.alert(saveResult.error || "Failed to save your favourite. Please try again.");
        return;
      }

      navigateToPrintFrameSelection();
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    await authService.googleAuth();
  };


  return (
    <>
      <StationThemeLayout
        className="personHobbySection personHobbySection--saveFavorite p-0"
        station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        <div className="selectBrand">
          <div className="saveFavoriteContent">
            <aside
              className={`saveFavoritePreviewCard shadow-sm${
                isTransparentArtwork ? " saveFavoritePreviewCard--transparent" : ""
              }`}
              aria-label={`Generated ${artDisplayName} preview`}
            >
              <span className={`saveFavoritePreviewImgWrap${previewLoaded ? " is-loaded" : ""}`}>
                {!previewLoaded && <span className="saveFavoritePreviewShimmer" aria-hidden="true" />}
                <img
                  src={selectedImageUrl}
                  alt={`Generated ${artDisplayName}`}
                  onLoad={() => setPreviewLoaded(true)}
                  onError={() => setPreviewLoaded(true)}
                />
              </span>
            </aside>

            <div className={`heading${savePhase === "claim-instructions" ? " saveFavoriteClaimHeading" : ""}`}>
              {saveHeadings[savePhase]}
            </div>

            <div className={`btnSaveFavorite${showActionButtons ? "" : " is-hidden"}`}>
              <button className="btnYes" onClick={handleYes} disabled={isUpdatingOrder}>
                Yes
              </button>
              <button className="noBTN" onClick={handleNo} disabled={isUpdatingOrder}>
                No
              </button>
            </div>
          </div>
        </div>
      </StationThemeLayout>

      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}

      {signinModal && (
        <SigninModal
          show={signinModal}
          onHide={() => {
            setSigninModal(false);
          }}
          setSignupModal={setSignupModal}
          setForgotPasswordModal={setForgotPasswordModal}
          setSigninModal={setSigninModal}
          onAuthSuccess={handleAuthSuccess}
          onGoogleAuth={handleGoogleAuth}
        />
      )
      }
      {
        forgotPasswordModal && (
          <ForgotPasswordModal
            show={forgotPasswordModal}
            onHide={() => setForgotPasswordModal(false)}
            setForgotPasswordModal={setForgotPasswordModal}
            setSigninModal={setSigninModal}
            setSignupModal={setSignupModal}
            setVerificationModal={setVerificationModal}
          />
        )
      }
      {
        verificationModal && (
          <VerificationModal
            show={verificationModal}
            onHide={() => setVerificationModal(false)}
            setResetModal={setResetModal}
            setVerificationModal={setVerificationModal}
          />
        )
      }
      {
        resetModal && (
          <ResetModal
            show={resetModal}
            onHide={() => setResetModal(false)}
            setResetModal={setResetModal}
            setSuccessModal={setSuccessModal}
          />
        )
      }
      {
        successModal && (
          <SuccessModal
            show={successModal}
            onHide={() => setSuccessModal(false)}
            setSuccessModal={setSuccessModal}
            setSigninModal={setSigninModal}
          />
        )
      }

      {
        signupModal && (
          <SignupModal
            show={signupModal}
            onHide={() => setSignupModal(false)}
            setSignupModal={setSignupModal}
            setSigninModal={setSigninModal}
            setEmailVerificationModal={setEmailVerificationModal}
            onAuthSuccess={handleAuthSuccess}
            onGoogleAuth={handleGoogleAuth}
          />
        )
      }
      {
        emailVerificationModal && (
          <EmailVerificationModal
            show={emailVerificationModal}
            onHide={() => setSignupModal(false)}
            setSaveModal={setSaveModal}
          />
        )
      }

      {
        (loading || saveModal) &&
        <LoaderSaveModal
          show={loading || saveModal}
          loading={loading}
          success={false}
          onHide={() => {
            setLoading(false);
            setSaveModal(false);
          }}
        />
      }
    </>
  );
};

export default SaveFavorite;
